import { Skeleton } from 'antd'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import useResizeObserver from 'use-resize-observer'

import { CreateAlertDrawer } from 'features/alerts/features/create-alert-drawer'
import CatalogDetailContent from 'features/catalog/features/detail/components/CatalogDetailContent'
import { CatalogDetailHeaderActions } from 'features/catalog/features/detail/components/CatalogDetailHeaderActions/CatalogDetailHeaderActions'
import { useCatalogDetailHeaderLinks } from 'features/catalog/features/detail/hooks/use-catalog-detail-header-links'
import { useCatalogDetailProjectId } from 'features/catalog/features/detail/hooks/use-catalog-detail-project-id'
import { FocusListOption } from 'features/catalog/types'
// bully ignore[no-cross-feature-private-imports: the established v1 filter-hash link seam use-overview.ts's Overview/Container.tsx already uses for this exact catalog detail page (screenLinkService construction). follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { useScreenLinkService } from 'features/project/url-params/service'
import { Filters } from 'types/project'

interface FocusDetailProps {
  lens: string
  item: FocusListOption
  setSubthemeValue: (value: string | null) => void
  subthemeName: string | null
  localFilters: Filters
  isLoading?: boolean
}

const CatalogDetail: React.FC<FocusDetailProps> = ({
  lens,
  item,
  setSubthemeValue,
  subthemeName,
  localFilters,
  isLoading = false,
}) => {
  const { ref, height = 1 } = useResizeObserver<HTMLDivElement>()
  const projectId = useCatalogDetailProjectId()
  const screenLinkService = useScreenLinkService()

  // The item-scoped filters (page-level filters + this row's own identity) —
  // needed both by CatalogDetailContent's chart/summary queries and by the
  // header's View-in-Feedback/Charts/Keywords links, so it's computed once
  // here rather than a second time inside each.
  const localFilterValues = useMemo(() => {
    const values = [
      ...localFilters.values,
      { field: lens, values: item.identifiers },
    ]
    // Ungrouped per-country source child: scope the summary to that country.
    // "Unknown" maps to the null/empty bucket which can't be matched by a
    // value-based IN filter, so we skip it and fall back to the source aggregate.
    if (item.country && item.country !== 'Unknown') {
      values.push({ field: 'country', values: [item.country] })
    }
    return { ...localFilters, values }
  }, [localFilters, lens, item.identifiers, item.country])

  const headerLinks = useCatalogDetailHeaderLinks({
    screenLinkService,
    lens,
    filters: localFilterValues,
    projectId,
  })

  return (
    <Container>
      <Header ref={ref}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'start',
              gap: 10,
              alignItems: 'center',
            }}
          >
            <div style={{ marginBottom: 0, fontSize: 22, fontWeight: 600 }}>
              {isLoading ? (
                <Skeleton active paragraph={false} title={{ width: 280 }} />
              ) : (
                item.value
              )}
            </div>
          </div>
          {!isLoading && (
            <CatalogDetailHeaderActions
              links={headerLinks}
              projectId={projectId}
            />
          )}
        </div>
      </Header>
      <Content $headerHeight={height}>
        <CatalogDetailContent
          item={item}
          lens={lens}
          localFilterValues={localFilterValues}
          subthemeName={subthemeName}
          setSubthemeValue={setSubthemeValue}
          isLoading={isLoading}
        />
      </Content>
      <CreateAlertDrawer />
    </Container>
  )
}

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding: 20px;
  background: white;
`

const Header = styled.div``

const Content = styled.div<{ $headerHeight?: number }>`
  height: calc(100% - ${(props) => props.$headerHeight}px);
  overflow: auto;
  display: flex;
`

export default CatalogDetail
